import {
  and,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  or,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import {
  DataProvider,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { DateTime } from "luxon";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { firestore } from "../firebase";
import { AutovioCalendarEvent } from "../model/autovioCalendarEvents";
import { autovioCalendarEventConverter } from "./converter/autovioCalendarEventConverter";
import { QueryFilterConstraint } from "@firebase/firestore";
import { TrackRunningQueriesWrapper } from "./trackRunningQueriesWrapper";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { isDateRange, isValidInterval } from "../model/DateRange";
import {
  examMatchesBranch,
  examMatchesPeriod,
  examMatchesSearch,
  examMatchesType,
  isExamPeriodFilter,
  isExamOnlyTypeFilter,
  type ExamTypeFilter,
} from "../utils/examFilters";
import { studentsProvider } from "./studentsProvider";

class CalendarEventsProvider extends AbstractDataProvider<AutovioCalendarEvent> {
  async getOne(_resource: string, { id }: GetOneParams): Promise<GetOneResult<AutovioCalendarEvent>> {
    const snapshot = await getDoc(doc(firestore, `calendar_events/${id}`));
    const event = autovioCalendarEventConverter.fromFirestore(snapshot);
    return { data: event };
  }

  async getList(
    _resource: string,
    { filter, sort, meta, pagination }: GetListParams,
  ): Promise<GetListResult<AutovioCalendarEvent>> {
    const {
      dateRange,
      allDates,
      drivingSchoolId,
      instructorId,
      studentId,
      type,
      branchId,
      vehicleIds,
      examPeriod,
      examType,
      examBranchId,
      q,
      ...clientSideFilter
    } = filter;
    const { includeDeleted, withLessonsCanceledAtShortNotice, instructorBranchIdsByInstructorId, instructorNamesById } =
      meta ?? {};
    if (!drivingSchoolId) {
      throw new Error("filter.drivingSchoolId is required");
    }
    if (typeof drivingSchoolId !== "string") {
      throw new Error(`Invalid filter.drivingSchoolId: ${JSON.stringify(drivingSchoolId)} -- must be a string`);
    }
    if (vehicleIds && typeof vehicleIds !== "string") {
      throw new Error(`Invalid filter.vehicleIds: ${JSON.stringify(vehicleIds)} -- must be a string`);
    }
    if (allDates !== undefined) {
      if (allDates !== true) {
        throw new Error(`Invalid filter.allDates: ${JSON.stringify(allDates)} -- must be true`);
      }
      if (dateRange) {
        throw new Error("filter.allDates cannot be combined with filter.dateRange");
      }
      if (!isExamOnlyTypeFilter(type)) {
        throw new Error("filter.allDates is only supported for exam-only queries");
      }
    }
    if (examPeriod && !isExamPeriodFilter(examPeriod)) {
      throw new Error(
        `Invalid filter.examPeriod: ${JSON.stringify(examPeriod)} -- expected: "currentWeek", "lastWeek" or YYYY-MM`,
      );
    }
    if (q && typeof q !== "string") {
      throw new Error(`Invalid filter.q: ${JSON.stringify(q)} -- must be a string`);
    }
    if (examType && examType !== "TheoryExam" && examType !== "PracticalExam") {
      throw new Error(`Invalid filter.examType: ${JSON.stringify(examType)}`);
    }
    if (examBranchId && typeof examBranchId !== "string") {
      throw new Error(`Invalid filter.examBranchId: ${JSON.stringify(examBranchId)} -- must be a string`);
    }
    const queryFilterConstraints: Array<QueryFilterConstraint> = [
      where("drivingSchoolUid", "==", drivingSchoolId),
      ...(vehicleIds ? [where("resources", "array-contains", vehicleIds)] : []),
    ];
    if (!allDates) {
      if (!dateRange) {
        if (!studentId) {
          throw new Error("filter.dateRange, filter.allDates or filter.studentId is required");
        }
      } else if (isDateRange(dateRange)) {
        queryFilterConstraints.push(
          where("start", ">=", Timestamp.fromDate(dateRange[0].toDate())),
          where("start", "<", Timestamp.fromDate(dateRange[1].nextDay.toDate())),
        );
      } else if (isValidInterval(dateRange)) {
        queryFilterConstraints.push(
          where("start", ">=", Timestamp.fromDate(dateRange.start.toJSDate())),
          where("start", "<", Timestamp.fromDate(dateRange.end.toJSDate())),
        );
      } else {
        throw new Error(`Invalid filter.dateRange: ${JSON.stringify(dateRange)}`);
      }
    }
    if (!includeDeleted) {
      queryFilterConstraints.push(where("deleted", "==", false));
    }
    if (instructorId) {
      if (typeof instructorId !== "string") {
        throw new Error(`Invalid filter.instructorId: ${JSON.stringify(instructorId)} -- must be a string`);
      }
      if (!instructorId) {
        throw new Error(`Invalid filter.instructorId: ${JSON.stringify(instructorId)} -- must not be the empty string`);
      }
      queryFilterConstraints.push(where("derived.instructorUids", "array-contains", instructorId));
    }
    if (studentId) {
      if (typeof studentId !== "string") {
        throw new Error(`Invalid filter.studentId: ${JSON.stringify(studentId)} -- must be a string`);
      }
      if (!studentId) {
        throw new Error(`Invalid filter.studentId: ${JSON.stringify(studentId)} -- must not be the empty string`);
      }
      queryFilterConstraints.push(where("derived.studentUids", "array-contains", studentId));
    }
    if (type) {
      const validTypes = ["TheoryLesson", "ASFCourseSession", "TheoryExam", "PracticalExam"];
      let types: Array<string>;
      if (typeof type === "string") {
        if (!validTypes.includes(type)) {
          throw new Error(
            `Invalid filter.type: ${JSON.stringify(type)} -- valid types: ${validTypes
              .map((it) => JSON.stringify(it))
              .join(", ")}`,
          );
        }
        types = [type];
      } else if (Array.isArray(type)) {
        if (type.length === 0) {
          console.warn(`Unexpected filter.type: ${JSON.stringify(type)} -- returning empty result`);
          return { data: [], total: 0 };
        }
        if (!type.every((it) => validTypes.includes(it))) {
          throw new Error(
            `Invalid filter.type: ${JSON.stringify(type)} -- valid types: ${validTypes
              .map((it) => JSON.stringify(it))
              .join(", ")}`,
          );
        }
        types = type;
      } else {
        throw new Error(
          `Invalid filter.type: ${JSON.stringify(type)} -- must be either a string or an array of strings`,
        );
      }
      const typeConstraints = types.map((it): QueryFilterConstraint => {
        if (it === "TheoryLesson") {
          return where("type", "==", "TheoryLesson");
        } else if (it === "ASFCourseSession") {
          return where("type", "==", "ASFCourseSession");
        } else if (it === "TheoryExam") {
          return and(where("type", "==", "DrivingLesson"), where("drivingLessonType", "==", "theoretischePruefung"));
        } else if (it === "PracticalExam") {
          return and(where("type", "==", "DrivingLesson"), where("drivingLessonType", "==", "praktischePruefung"));
        } else {
          throw new Error(`Not implemented: Convert type ${JSON.stringify(it)} to query constraint`);
        }
      });
      if (types.length === 1) {
        queryFilterConstraints.push(typeConstraints[0]);
      } else {
        queryFilterConstraints.push(or(...typeConstraints));
      }
    }
    if (branchId) {
      if (typeof branchId !== "string") {
        throw new Error(`Invalid filter.branchId: ${JSON.stringify(type)} -- must be a string`);
      }
      queryFilterConstraints.push(where("branchId", "==", branchId));
    }
    const calendarEventsRef = collection(firestore, "calendar_events");
    let docs = (await getDocs(query(calendarEventsRef, and(...queryFilterConstraints)))).docs;
    if (includeDeleted) {
      docs = docs.filter((doc) => !doc.data().replacedBy);
    }
    let eventsFromFirestore = docs.map((doc) => autovioCalendarEventConverter.fromFirestore(doc));

    if (!includeDeleted) {
      eventsFromFirestore = eventsFromFirestore.filter((event) => {
        if (withLessonsCanceledAtShortNotice && event.student?.canceledAtShortNotice) {
          return true;
        }
        return !["rejected", "declined", "canceled"].includes(event.student?.rsvp);
      });
    }
    if (examPeriod) {
      const now = DateTime.now();
      eventsFromFirestore = eventsFromFirestore.filter((event) => examMatchesPeriod(event, examPeriod, now));
    }
    if (q) {
      const { data: matchingStudents } = await studentsProvider.getList("students", {
        filter: { drivingSchoolId, q },
        pagination: { page: 1, perPage: 0 },
        sort: { field: "name", order: "ASC" },
      });
      const studentNamesById = Object.fromEntries(matchingStudents.map((student) => [student.id, student.name]));
      eventsFromFirestore = eventsFromFirestore.filter((event) =>
        examMatchesSearch(event, q, studentNamesById ?? {}, instructorNamesById ?? {}),
      );
    }
    if (examType) {
      eventsFromFirestore = eventsFromFirestore.filter((event) => examMatchesType(event, examType as ExamTypeFilter));
    }
    if (examBranchId) {
      eventsFromFirestore = eventsFromFirestore.filter((event) =>
        examMatchesBranch(event, examBranchId, instructorBranchIdsByInstructorId ?? {}),
      );
    }
    return applyPagination(applySort(applyFilter(eventsFromFirestore, clientSideFilter), sort), pagination);
  }

  async getManyReference(
    resource: string,
    { target, id, filter, sort, pagination, meta }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<AutovioCalendarEvent>> {
    if (!["drivingSchoolId", "vehicleIds"].includes(target)) {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId" or "vehicleIds"`);
    }
    // Paranoia check ...
    if (filter[target] && filter[target] !== id) {
      throw new Error(
        `filter.${target}: ${JSON.stringify(filter[target])} does not match the given target: ${JSON.stringify(target)} and id: ${JSON.stringify(id)}`,
      );
    }
    if (target !== "drivingSchoolId" && !filter.drivingSchoolId) {
      throw new Error(`filter.drivingSchoolId is required when target is ${JSON.stringify(target)}`);
    }
    return this.getList(resource, { filter: { ...filter, [target]: id }, sort, pagination, meta });
  }

  async update(
    resource: string,
    { id, data }: UpdateParams<AutovioCalendarEvent>,
  ): Promise<UpdateResult<AutovioCalendarEvent>> {
    const keys = Object.keys(data);
    if (keys.length !== 1 || !["theoryExamResult", "theoryExamResults", "practicalExamResult"].includes(keys[0])) {
      throw new Error(
        `Unexpected data: ${JSON.stringify(data)} -- expected: {theoryExamResult: ...}, {theoryExamResults: ...} or {practicalExamResult: ...}`,
      );
    }
    const key = keys[0];
    const value = data[key];
    let updateData: Record<string, unknown>;
    if (key === "theoryExamResults") {
      if (value != null && (typeof value !== "object" || Array.isArray(value))) {
        throw new Error(
          `Unexpected theoryExamResults: ${JSON.stringify(value)} -- expected an object mapping training IDs to results`,
        );
      }
      const results = (value ?? {}) as Record<string, "passed" | "failed">;
      for (const result of Object.values(results)) {
        if (result !== "passed" && result !== "failed") {
          throw new Error(
            `Unexpected theoryExamResults value: ${JSON.stringify(result)} -- expected: "passed" or "failed"`,
          );
        }
      }
      const resultValues = Object.values(results);
      updateData =
        resultValues.length > 0
          ? {
              theoryExamResults: results,
              theoryExamResult: resultValues.includes("failed") ? "failed" : "passed",
            }
          : { theoryExamResults: deleteField(), theoryExamResult: deleteField() };
    } else {
      if (value && value !== "passed" && value !== "failed") {
        throw new Error(`Unexpected ${key}: ${JSON.stringify(value)} -- expected: "passed" or "failed"`);
      }
      updateData = { [key]: value || deleteField() };
    }
    console.info(`Updating calendar_events/${id} ...`, updateData);
    await setDoc(doc(firestore, `calendar_events/${id}`), updateData, { merge: true });
    return this.getOne(resource, { id });
  }
}

export const calendarEventsProvider = new TrackRunningQueriesWrapper(new CalendarEventsProvider() as DataProvider);
